/**
 * Runners List Page.
 *
 * Displays enrolled runners with status, OS, certificate info.
 * Provides enrollment initiation via a modal.
 *
 * Runners are on-premises executors for MCP tools (previously "local agents").
 *
 * Sprint: 40, Task: S40-C02
 */

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useI18n } from '@/i18n';
import {
  Button,
  Card,
  Spinner,
  StatusBadge,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui';
import { useRunners } from '@/features/mcp';
import type { RunnerStatusResponse, RunnerStatus, CertificateStatus } from '@/features/mcp';
import { Cpu, Plus, Monitor, Clock, Download, Search, ChevronLeft, ChevronRight } from 'lucide-react';
import { EnrollmentModal } from './EnrollmentModal';

// =============================================================================
// Helpers
// =============================================================================

function runnerStatusType(status: RunnerStatus) {
  switch (status) {
    case 'online':
      return 'healthy' as const;
    case 'offline':
      return 'default' as const;
    case 'error':
      return 'error' as const;
    case 'pending':
      return 'warning' as const;
    default:
      return 'default' as const;
  }
}

function certStatusType(status: CertificateStatus) {
  switch (status) {
    case 'active':
      return 'healthy' as const;
    case 'pending':
      return 'warning' as const;
    case 'expired':
      return 'error' as const;
    case 'revoked':
      return 'error' as const;
    default:
      return 'default' as const;
  }
}

function formatRelativeTime(dateStr: string | null): string {
  if (!dateStr) return '—';
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMin = Math.floor(diffMs / 60000);
  if (diffMin < 1) return 'Just now';
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHours = Math.floor(diffMin / 60);
  if (diffHours < 24) return `${diffHours}h ago`;
  const diffDays = Math.floor(diffHours / 24);
  return `${diffDays}d ago`;
}

function osLabel(os: string): string {
  switch (os.toLowerCase()) {
    case 'darwin':
      return 'macOS';
    case 'linux':
      return 'Linux';
    case 'windows':
      return 'Windows';
    default:
      return os || '—';
  }
}

// =============================================================================
// Component
// =============================================================================

export function RunnersListPage() {
  const { t } = useI18n();
  const [showEnrollModal, setShowEnrollModal] = useState(false);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const pageSize = 50;

  const { data, isLoading, error } = useRunners({ page, pageSize, search: search || undefined });

  const runners = data?.runners ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Cpu className="w-6 h-6 text-primary" />
            {t('admin.runners.title')}
          </h1>
          <p className="text-muted-foreground mt-1">
            {t('admin.runners.subtitle')}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Link to="/integrations/download-agent">
            <Button variant="outline">
              <Download className="w-4 h-4 mr-2" />
              {t('admin.nav.downloadAgent')}
            </Button>
          </Link>
          <Button onClick={() => setShowEnrollModal(true)}>
            <Plus className="w-4 h-4 mr-2" />
            {t('admin.runners.enroll')}
          </Button>
        </div>
      </div>

      {/* Search */}
      <div className="relative max-w-sm">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <input
          type="text"
          placeholder={t('admin.runners.searchPlaceholder')}
          value={search}
          onChange={(e) => { setSearch(e.target.value); setPage(1); }}
          className="w-full pl-9 pr-3 py-2 border rounded-md bg-background text-sm"
        />
      </div>

      {/* Content */}
      {isLoading ? (
        <div className="flex items-center justify-center py-20">
          <Spinner />
        </div>
      ) : error ? (
        <Card className="p-8 text-center text-destructive">
          Failed to load runners
        </Card>
      ) : runners.length === 0 ? (
        <Card className="p-12 text-center">
          <Cpu className="w-12 h-12 mx-auto text-muted-foreground/50 mb-4" />
          <p className="text-muted-foreground">{t('admin.runners.empty')}</p>
          <Button variant="outline" className="mt-4" onClick={() => setShowEnrollModal(true)}>
            <Plus className="w-4 h-4 mr-2" />
            {t('admin.runners.enroll')}
          </Button>
        </Card>
      ) : (
        <>
          <Card>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t('common.name')}</TableHead>
                  <TableHead>{t('common.status')}</TableHead>
                  <TableHead>{t('admin.runners.os')}</TableHead>
                  <TableHead>{t('admin.runners.certificate')}</TableHead>
                  <TableHead>{t('admin.runners.lastSeen')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {runners.map((runner: RunnerStatusResponse) => (
                  <TableRow key={runner.agent_id}>
                    <TableCell>
                      <Link
                        to={`/integrations/runners/${runner.agent_id}`}
                        className="font-medium text-primary hover:underline"
                      >
                        {runner.name}
                      </Link>
                      {runner.hostname && (
                        <p className="text-xs text-muted-foreground flex items-center gap-1 mt-0.5">
                          <Monitor className="w-3 h-3" />
                          {runner.hostname}
                        </p>
                      )}
                    </TableCell>
                    <TableCell>
                      <StatusBadge
                        status={runnerStatusType(runner.status as RunnerStatus)}
                        label={t(`admin.runners.status.${runner.status}`)}
                      />
                    </TableCell>
                    <TableCell>
                      <span className="text-sm">{osLabel(runner.os)}</span>
                      {runner.arch && (
                        <span className="text-xs text-muted-foreground ml-1">({runner.arch})</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <StatusBadge
                        status={certStatusType(runner.certificate_status as CertificateStatus)}
                        label={t(`admin.runners.certStatus.${runner.certificate_status}`)}
                      />
                    </TableCell>
                    <TableCell>
                      <span className="text-xs text-muted-foreground flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {formatRelativeTime(runner.last_seen)}
                      </span>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between mt-4">
              <span className="text-sm text-muted-foreground">
                {t('admin.runners.pagination.showing', { from: (page - 1) * pageSize + 1, to: Math.min(page * pageSize, total), total })}
              </span>
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <span className="text-sm">{page} / {totalPages}</span>
                <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          )}
        </>
      )}

      {/* Enrollment Modal */}
      {showEnrollModal && (
        <EnrollmentModal onClose={() => setShowEnrollModal(false)} />
      )}
    </div>
  );
}
